import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { staffMovementApi } from '../../api/staff-movement';
import { employeeApi } from '../../api/hr';
import { departmentsApi } from '../../api/admin/departments';
import { positionsApi } from '../../api/admin/positions';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { EmployeePicker } from '../../components/EmployeePicker';
import { Eye, Send, FileSearch, Check, X, Ban } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { ReturnAction } from '../../components/ReturnAction';
import { usePermission } from '../../auth/AuthContext';
import { useToast } from '../../components/Toast';

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  promotion: 'Promotion', transfer: 'Transfer', position_change: 'Position Change', department_change: 'Department Change',
  duty_station_change: 'Duty Station Change', supervisor_change: 'Supervisor Change', grade_step_change: 'Grade/Step Change',
  salary_adjustment: 'Salary Adjustment', temporary_assignment: 'Temporary Assignment', other: 'Other',
};
const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  approved: 'Approved', approved_pending_effective: 'Approved - Pending Effective Date', implemented: 'Implemented', rejected: 'Rejected', cancelled: 'Cancelled',
};

export function StaffMovementPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canApprove = usePermission('staff_movement.approve');
  const canImplement = usePermission('staff_movement.implement');
  const canViewSalary = usePermission('compensation.view_history');

  const allQuery = useQuery({ queryKey: ['staff-movements-all'], queryFn: () => staffMovementApi.listAll() });
  const [showForm, setShowForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const currentEmployeeQuery = useQuery({ queryKey: ['movement-current-employee', selectedEmployeeId], queryFn: () => employeeApi.getById(selectedEmployeeId!), enabled: !!selectedEmployeeId });
  const departmentsQuery = useQuery({ queryKey: ['admin-departments'], queryFn: () => departmentsApi.list(), enabled: showForm });
  const positionsQuery = useQuery({ queryKey: ['admin-positions'], queryFn: () => positionsApi.list(), enabled: showForm });

  const { register, handleSubmit, watch, reset } = useForm<Record<string, string>>();
  const movementType = watch('movementType');

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => staffMovementApi.create({
      employeeId: Number(v.employeeId), movementType: v.movementType, effectiveDate: v.effectiveDate, justification: v.justification, reason: v.reason || undefined,
      proposedPositionId: v.proposedPositionId ? Number(v.proposedPositionId) : undefined, proposedDepartmentId: v.proposedDepartmentId ? Number(v.proposedDepartmentId) : undefined,
      proposedGrade: v.proposedGrade || undefined, proposedSalary: v.proposedSalary ? Number(v.proposedSalary) : undefined,
      promotionBasis: v.promotionBasis || undefined, isTemporary: movementType === 'temporary_assignment', temporaryEndDate: v.temporaryEndDate || undefined,
    }),
    onSuccess: (result) => { showSuccess(`Movement request created - ${result.data.movementReference}`); queryClient.invalidateQueries({ queryKey: ['staff-movements-all'] }); setShowForm(false); reset(); setSelectedEmployeeId(null); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create movement request.'),
  });

  const submitMutation = useMutation({ mutationFn: (id: number) => staffMovementApi.submit(id), onSuccess: () => { showSuccess('Submitted.'); queryClient.invalidateQueries({ queryKey: ['staff-movements-all'] }); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const reviewMutation = useMutation({ mutationFn: (id: number) => staffMovementApi.supervisorReview(id), onSuccess: () => { showSuccess('Moved to HR review.'); queryClient.invalidateQueries({ queryKey: ['staff-movements-all'] }); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const decideMutation = useMutation({
    mutationFn: ({ id, decision, comment }: { id: number; decision: 'approved' | 'rejected'; comment: string }) => staffMovementApi.decide(id, decision, comment),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['staff-movements-all'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const cancelMutation = useMutation({ mutationFn: (id: number) => staffMovementApi.cancel(id), onSuccess: () => { showSuccess('Cancelled.'); queryClient.invalidateQueries({ queryKey: ['staff-movements-all'] }); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });
  const applyDueMutation = useMutation({
    mutationFn: () => staffMovementApi.applyDue(),
    onSuccess: (result) => { showSuccess(`${result.data.implemented} due movement(s) implemented.`); queryClient.invalidateQueries({ queryKey: ['staff-movements-all'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const currentEmployee = currentEmployeeQuery.data?.data;

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader
        title="Staff Movement, Promotion & Transfer"
        actions={
          <span className="row-actions">
            {canImplement && <button className="btn-primary" onClick={() => applyDueMutation.mutate()} disabled={applyDueMutation.isPending}>Apply Due Movements</button>}
            <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Movement Request'}</button>
          </span>
        }
      />

      {showForm && (
        <section className="card">
          <h2>New Movement Request</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Employee<EmployeePicker {...register('employeeId', { required: true, valueAsNumber: true, onChange: (e) => setSelectedEmployeeId(Number(e.target.value) || null) })} /></label>

            {currentEmployee && (
              <>
                <h3>Current Employment Details</h3>
                <div className="project-header-grid">
                  <StaticField label="Name" value={currentEmployee.full_name} />
                  <StaticField label="Position" value={currentEmployee.position_name ?? '-'} />
                  <StaticField label="Department" value={currentEmployee.department_name ?? '-'} />
                  <StaticField label="Duty Station" value={currentEmployee.duty_station_name ?? '-'} />
                  <StaticField label="Supervisor" value={currentEmployee.supervisor_name ?? '-'} />
                  <StaticField label="Grade" value={currentEmployee.grade ?? '-'} />
                  {canViewSalary && <StaticField label="Current Salary" value={currentEmployee.base_salary ? `${currentEmployee.salary_currency ?? 'NGN'} ${Number(currentEmployee.base_salary).toLocaleString()}` : '-'} />}
                </div>
              </>
            )}

            <h3>Proposed Employment Details</h3>
            <label>
              Movement Type
              <select {...register('movementType', { required: true })}>
                <option value="">- select -</option>
                {Object.entries(MOVEMENT_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label>Effective Date<input type="date" {...register('effectiveDate', { required: true })} /></label>

            {(movementType === 'position_change' || movementType === 'promotion') && (
              <label>
                New Position
                <select {...register('proposedPositionId')}>
                  <option value="">- select -</option>
                  {positionsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </label>
            )}
            {(movementType === 'department_change' || movementType === 'transfer') && (
              <label>
                New Department
                <select {...register('proposedDepartmentId')}>
                  <option value="">- select -</option>
                  {departmentsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
            )}
            {(movementType === 'grade_step_change' || movementType === 'promotion') && (
              <label>New Grade<input {...register('proposedGrade')} /></label>
            )}
            {(movementType === 'salary_adjustment' || movementType === 'promotion') && canViewSalary && (
              <label>New Salary<input type="number" {...register('proposedSalary')} /></label>
            )}
            {movementType === 'promotion' && (
              <label>
                Promotion Basis
                <select {...register('promotionBasis')}>
                  <option value="">- select -</option>
                  <option value="performance">Performance</option><option value="increased_responsibility">Increased Responsibility</option>
                  <option value="vacancy_availability">Vacancy/Position Availability</option><option value="organisational_need">Organisational Need</option>
                  <option value="career_progression">Career Progression</option><option value="other">Other</option>
                </select>
              </label>
            )}
            {movementType === 'temporary_assignment' && (
              <label>Temporary Assignment End Date<input type="date" {...register('temporaryEndDate')} /></label>
            )}

            <label>Reason<input {...register('reason')} placeholder="e.g. Promotion following performance review" /></label>
            <label>Detailed Justification<textarea rows={2} {...register('justification', { required: true })} /></label>

            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft'}</button><button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button></div>
          </form>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Movement Requests" />
        <div className="panel-body">
        {allQuery.data && (
          <DataTable
            rows={allQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No movement requests yet."
            columns={[
              { header: 'Reference', render: (r) => r.movement_reference },
              { header: 'Employee', render: (r) => r.employee_name },
              { header: 'Type', render: (r) => MOVEMENT_TYPE_LABELS[r.movement_type] ?? r.movement_type },
              { header: 'Effective Date', render: (r) => r.effective_date },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              {
                header: '',
                render: (r) => (
                  <span className="row-actions">
                    <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/hr/staff-movement/${r.id}`)} />
                    {r.status === 'draft' && <IconActionButton icon={Send} label="Submit" onClick={() => submitMutation.mutate(r.id)} />}
                    {r.status === 'submitted' && <IconActionButton icon={FileSearch} label="Move to HR Review" onClick={() => reviewMutation.mutate(r.id)} />}
                    {r.status === 'under_review' && canApprove && (
                      <>
                        <IconActionButton icon={Check} label="Approve" onClick={() => { const c = window.prompt('Approval comment:'); if (c) decideMutation.mutate({ id: r.id, decision: 'approved', comment: c }); }} />
                        <IconActionButton icon={X} label="Reject" danger onClick={() => { const c = window.prompt('Rejection reason:'); if (c) decideMutation.mutate({ id: r.id, decision: 'rejected', comment: c }); }} />
                        <ReturnAction onReturn={(comment) => staffMovementApi.return(r.id, comment)} queryKeysToInvalidate={[['staff-movements-all']]} />
                      </>
                    )}
                    {['draft', 'submitted', 'under_review', 'approved', 'approved_pending_effective'].includes(r.status) && (
                      <IconActionButton icon={Ban} label="Cancel" danger onClick={() => { if (window.confirm('Cancel this movement request?')) cancelMutation.mutate(r.id); }} />
                    )}
                    <Link to={`/hr/staff/${r.employee_id}`}>View Employee</Link>
                  </span>
                ),
              },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}

function StaticField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}
